import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { SvgIcon, Text } from '@cinch-labs/design-system'
import { OpeningHours } from '../../hooks/types'
import { formatOpeningHours } from '../../utils/format-opening-hours'
import styles from './opening-hours-drawer.module.css'

type OpeningHoursDrawerProps = {
  storeName: string
  openingHours: OpeningHours
  onClose: () => void
}

export const OpeningHoursDrawer: React.FC<OpeningHoursDrawerProps> = ({
  storeName,
  openingHours,
  onClose,
}) => {
  useEffect(() => {
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = originalOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  return createPortal(
    <div
      className={styles.overlay}
      onClick={onClose}
      data-testid="opening-hours-drawer-overlay"
    >
      <div
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby="opening-hours-drawer-title"
        onClick={(event) => event.stopPropagation()}
        data-testid="opening-hours-drawer"
      >
        <div className={styles.header}>
          <Text
            id="opening-hours-drawer-title"
            element="h2"
            fontWeight="bold"
            className={styles.title}
          >
            {storeName} opening hours
          </Text>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close opening hours"
          >
            <SvgIcon name="cross" size={16} />
          </button>
        </div>

        <div className={styles.hoursList}>
          {formatOpeningHours(openingHours).map(({ day, hours }) => (
            <div key={day} className={styles.hoursRow}>
              <Text className={styles.day}>{day}</Text>
              <Text fontWeight="semibold" className={styles.hours}>
                {hours}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}
